import { timeoutPromise } from '@zajno/common/async/timeout';
import type { ILogger } from '@zajno/common/logger/types';

export interface GiveWayFetchOptions {
  /** How long a request may run before its caller gets a failure. */
  giveWayMs: number;
  /** Called after an OK answer to a request that gave way, once a repeat of that request is ready to be served from it. */
  onLateAnswer: () => void;
  /** Optional logger for a late answer that could not be held. */
  logger?: ILogger;
}

interface HeldAnswer {
  key: string;
  response: Response;
}

/**
 * A `fetch` whose callers stop waiting after `giveWayMs`, while the request itself keeps running.
 *
 * Giving way:
 * - The caller's promise rejects with a `TimeoutError` once `giveWayMs` passes without an answer.
 * - An answer, or a failure, before that passes through.
 *
 * Late answers:
 * - An OK answer to a request that gave way is held, then {@link GiveWayFetchOptions.onLateAnswer} fires.
 * - The held answer serves the next request with the same method, URL and body, once and with no network call.
 * - Any other request drops it.
 * - A late failure, a non-OK answer, or an abort of the request's signal holds nothing.
 * - A request passed as a `Request`, or with a body that is not a string, only gives way: its answer is never held.
 */
export function createGiveWayFetch({ giveWayMs, onLateAnswer, logger }: GiveWayFetchOptions): typeof globalThis.fetch {
  let held: HeldAnswer | undefined;

  const holdLateAnswer = async (request: Promise<Response>, key: string, signal: AbortSignal | null | undefined): Promise<void> => {
    try {
      const response = await request;
      if (!response.ok) {
        return;
      }

      // Read in full before the repeat request aborts `signal`, which would error an unread body.
      const body = await response.arrayBuffer();
      if (signal?.aborted) {
        return;
      }

      held = {
        key,
        response: new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers }),
      };
      onLateAnswer();
    } catch (error) {
      logger?.log('A late answer was dropped.', error);
    }
  };

  return async (input, init) => {
    const key = requestKey(input, init);
    const answer = held;
    held = undefined;
    if (key !== undefined && answer?.key === key) {
      return answer.response;
    }

    // Looked up per request, so a `fetch` wrapper installed later still applies.
    const request = globalThis.fetch(input, init);
    const { resolved, timedOut } = await timeoutPromise(request, giveWayMs);
    if (!timedOut) {
      return resolved!;
    }

    if (key !== undefined) {
      void holdLateAnswer(request, key, init?.signal);
    }
    throw new DOMException(`No answer within ${giveWayMs} ms`, 'TimeoutError');
  };
}

/** `undefined` for a request two calls cannot be compared by: one passed as a `Request`, or with a body that is not a string. */
function requestKey(input: Parameters<typeof globalThis.fetch>[0], init: RequestInit | undefined): string | undefined {
  if (input instanceof Request || (init?.body != null && typeof init.body !== 'string')) {
    return undefined;
  }
  return `${(init?.method ?? 'GET').toUpperCase()} ${String(input)}\n${init?.body ?? ''}`;
}
